import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";



import styled from "styled-components/macro";



import { useLoginLevel } from "Flocknote/stateHooks";



import API from "Signups/API.js";
import { getSignedUpViaID } from "Signups/SharedComponents/getSignedUpViaID";
import { getSignupErrorMessage } from "Signups/SharedComponents/getSignupErrorMessage";
import { useIsSignupClosed } from "Signups/SharedComponents/useIsSignupClosed";
import { useSignups } from "Signups/state";
import { AnimatePresence, motion } from "framer-motion";



import FnrButton from "UI/FnrButton3";



import CancelItemSignup from "./CancelItemSignup";
import CostInfoBadge from "./CostInfoBadge";
import CurrentlySignedUp from "./CurrentlySignedUp";
import Error from "./Error";
import NewUserForm from "./NewUserForm";
import NumberOfItems from "./NumberOfItems";
import ProgressBarArea from "./ProgressBarArea";
import RespondentEntryExtendedFields from "./RespondentEntryExtendedFields";
import { SignupItemWrapper, Title, TitleBar, TitleButtonWrapper } from "./Styles";
import { useShowNewUserForm } from "./stateHooks";
import { checkIfAnyItemTypeItemIsPaidFor } from "./utils/checkIfAnyItemTypeItemIsPaidFor";
import { convertExtendedFieldInputResponses } from "./utils/dataTranformations";
import { getExtendedFieldResponsesValidationErrors } from "./utils/fieldValidationHelpers";
import { getCurrentlySignedUpCount } from "./utils/getRespondentNumbers";
import { getUserForSignupSubmission } from "./utils/getUserForSignupSubmission";
import { getUserSignupItem } from "./utils/getUserSignupItem";
import { useIsSignedUpForItem } from "./utils/useIsSignedUpForItem";


SignupItemItemType.propTypes = {
  signupItem: PropTypes.object,
};

export default function SignupItemItemType({ signupItem }) {
  const [state, dispatch] = useSignups();
  const { expandedItemID, extendedFieldInputResponses } = state;
  const { ID, title, extendedFields } = signupItem;
  const isExpanded = expandedItemID === ID;
  const [status, setStatus] = useState(null);
  const [signupErrorMessage, setSignupErrorMessage] = useState(null);
  const isCurrentlySignedUp = useIsSignedUpForItem(signupItem.ID);
  const { isClosed } = useIsSignupClosed();

  const { maxSingleUserSlots, totalSlots, slotsTaken } = signupItem;
  const userSignupItem = getUserSignupItem(state.userSignupItems, ID);
  const numberOfPaidSlots = userSignupItem?.respondents?.filter(
    (r) => r.paymentStatus === "Paid" || r.paymentStatus === "MarkedAsPaid"
  )?.length;

  const multipleItemsAllowed =
    state.currentSignupList.options.allowMultipleItemSignups === 1;
  const hasPaidForAnyItemTypeItem = checkIfAnyItemTypeItemIsPaidFor(
    state.userSignupItems
  );

  const hasExtendedFields = signupItem?.extendedFields?.length > 0;

  // For a paid Items-type Signup, we hide the "Sign Up" button on other items if something has
  // been paid for. Otherwise this action cancels their paid item, which isn't allowed.
  const hideSignupButtonBecauseOfPaidStatus =
    !multipleItemsAllowed && hasPaidForAnyItemTypeItem;

  let availableSlots = totalSlots - slotsTaken;
  if (isCurrentlySignedUp) {
    // if the user is currently signed up, we need to account for their
    // signup slots already being included in the slotsTaken number.
    const currentlySignedUpSlots = getCurrentlySignedUpCount(
      state,
      signupItem.ID
    );
    availableSlots += currentlySignedUpSlots;
  }

  const maxSlots =
    availableSlots < maxSingleUserSlots ? availableSlots : maxSingleUserSlots;
  const isFull = maxSlots < 1;

  useEffect(() => {
    setStatus(null);
  }, [isExpanded]);

  const showNewUserForm = useShowNewUserForm(
    hasExtendedFields,
    Boolean(state?.user?.ID)
  );

  const getInitialNumSlots = () => {
    if (!state.userSignupItems) return 1;
    const userSignupItem = state.userSignupItems[signupItem.ID];
    return userSignupItem?.count || 1;
  };

  const [numberOfSlots, setNumberOfSlots] = useState(getInitialNumSlots);

  const setExpandedItemID = (ID) => {
    dispatch({
      type: "expandedItemID",
      ID,
    });
  };

  const handleSignupButtonClick = () => {
    if (numberOfSlots === 0) {
      submitCancelSignupToAPI();
    } else {
      submitSignupToAPI();
    }
  };

  const submitCancelSignupToAPI = () => {
    if (status === "loading") return;
    setStatus("loading");

    const signedUpViaID = getSignedUpViaID();
    const emailOrPhone = state.user.emailOrPhone || null;

    API.cancelSignupForItem2({ signupItemID: ID, signedUpViaID, emailOrPhone })
      .post()
      .then((resp) => {
        dispatch({
          type: "Item Signup Updated",
          userSignupItems: resp.userSignupItems,
          newCounts: resp.itemSlotsTaken,
          cancelled: true,
          paymentAmountNeeded: 0,
        });
        dispatch({
          type: "updateSlotsTaken",
          payload: {
            signupItemID: ID,
            slotsTaken: resp.itemSlotsTaken,
          },
        });
        setStatus(null);
        setExpandedItemID(null);
      })
      .catch((err) => {
        alert(getSignupErrorMessage(err.error));
        setStatus(null);
        setExpandedItemID(null);
      });
  };

  const setFieldValidationError = (errors) => {
    dispatch({ type: "setFieldValidationErrors", payload: errors });
  };

  const submitSignupToAPI = async () => {
    setFieldValidationError({});
    if (numberOfSlots < numberOfPaidSlots) {
      setStatus("error");
      setSignupErrorMessage(
        `You have already paid for ${numberOfPaidSlots} ${
          numberOfPaidSlots === 1 ? "item" : "items"
        }. Please select a higher number of items, or contact an administrator for help.`
      );
      return;
    }

    let respondent = { ...state.user };
    if (!respondent.userID) respondent.userID = respondent.ID;
    const fieldValidationErrors =
      await getExtendedFieldResponsesValidationErrors(
        [respondent],
        signupItem?.extendedFields,
        state?.extendedFieldInputResponses,
        true
      );
    if (fieldValidationErrors) {
      setFieldValidationError(fieldValidationErrors);
      return;
    }

    setStatus("loading");

    const user = getUserForSignupSubmission(state);

    API.signupForItem2({
      signupItemID: ID,
      user,
      numberOfSlots: numberOfSlots,
      signedUpViaID: getSignedUpViaID(),
      extendedFieldInputResponses: await convertExtendedFieldInputResponses(
        extendedFieldInputResponses,
        extendedFields
      ),
    })
      .post()
      .then((res) => {
        handleSuccessfulResponse(res);
      })
      .catch((err) => {
        if (err.error === "login_required") {
          // The main use case for "login_required" is if the user is not currently logged in, but have already signed up for this Signup list.
          // forcing them to log in allows them to accurately modify their existing signup.
          window.location = `/login?redirect=signup/${state.currentSignupList.ID}`;
          return;
        }

        setStatus("error");
        const errorCode = err.error;
        setSignupErrorMessage(getSignupErrorMessage(errorCode));

        if (errorCode === "no_user") {
          // this error code is often caused by an invalid email/phone, so we clear the user.
          dispatch({
            type: "user",
            payload: null,
          });
        }
      });
  };

  const updateSlotsTakenOnOldItem = (userID) => {
    if (!userID) return;

    // find item user used to be signed up for
    const oldItemId = parseInt(Object.keys(state.userSignupItems)[0]);
    const numberUserSignedUpFor = state.userSignupItems[oldItemId]?.count;

    // find how many total slots were taken for that item
    const oldSlotsTaken =
      state.currentSignupList.signupItems.find((item) => item.ID === oldItemId)
        ?.itemSlotsTaken || 0;

    // figure out how many should be signed up for that item now
    const difference = oldSlotsTaken - numberUserSignedUpFor;
    const newSlotsTaken = difference < 0 ? 0 : difference;
    if (oldItemId) {
      // update the UI
      dispatch({
        type: "updateSlotsTaken",
        payload: {
          signupItemID: oldItemId,
          slotsTaken: newSlotsTaken,
        },
      });
    }
  };

  const handleSuccessfulResponse = (res) => {
    if (!multipleItemsAllowed) {
      updateSlotsTakenOnOldItem(res.userID);
    }
    dispatch({
      type: "Item Signup Updated",
      userSignupItems: res.userSignupItems,
      newCounts: res.itemSlotsTaken,
      cancelled: false,
      paymentAmountNeeded: 0, // hides Pay Now while price recalculates
    });

    dispatch({
      type: "showConfetti",
      payload: true,
    });
    dispatch({
      type: "updateSlotsTaken",
      payload: {
        signupItemID: ID,
        slotsTaken: res.itemSlotsTaken,
      },
    });
    setStatus(null);
    setExpandedItemID(null);
  };

  const handleCloseClick = () => {
    setExpandedItemID(null);
    setSignupErrorMessage(null);
  };

  const SignupButton = () => {
    if (
      isCurrentlySignedUp ||
      isClosed ||
      isFull ||
      hideSignupButtonBecauseOfPaidStatus
    )
      return null;

    const buttonText =
      state.currentSignupList?.signupListVerbs?.actionName || "Sign Up";

    return (
      <FnrButton color="blue" onClick={() => setExpandedItemID(ID)}>
        {buttonText}
      </FnrButton>
    );
  };

  const SubmitButton = () => {
    return (
      <FnrButton
        color="green"
        size="large"
        onClick={handleSignupButtonClick}
        disabled={status === "loading"}
        loading={status === "loading"}
      >
        {isCurrentlySignedUp ? "Update Signup" : "Sign Me Up!"}
      </FnrButton>
    );
  };

  const renderExpandedContent = () => {
    if (status === "error") {
      return (
        <Error
          message={signupErrorMessage}
          handleCloseClick={handleCloseClick}
        />
      );
    } else if (showNewUserForm) {
      return <NewUserForm handleCloseClick={handleCloseClick} />;
    }
    return (
      <>
        <ExpandedContentMain>
          <NumberOfItems
            maxSlots={maxSlots}
            numberOfSlots={numberOfSlots}
            setNumberOfSlots={setNumberOfSlots}
            numberOfPaidSlots={numberOfPaidSlots}
            signupItem={signupItem}
            handleCloseClick={handleCloseClick}
          />
          {hasExtendedFields ? (
            <FieldsWrapper>
              <RespondentEntryExtendedFields
                respondent={state.user}
                fields={signupItem.extendedFields}
                signupItem={signupItem}
              />
            </FieldsWrapper>
          ) : null}
        </ExpandedContentMain>

        <SubmitWrapper>
          <CancelItemSignup
            signupItemID={ID}
            closeItem={() => setExpandedItemID(null)}
          />
          <SubmitButton />
        </SubmitWrapper>
      </>
    );
  };

  return (
    <SignupItemWrapper>
      <TitleBar>
        <Title>{title}</Title>
        <TitleButtonWrapper>
          {isExpanded ? null : SignupButton()}
        </TitleButtonWrapper>
      </TitleBar>

      <CostInfoBadge
        signupItem={signupItem}
        isCurrentlySignedUp={isCurrentlySignedUp}
      />

      <AnimatePresence>
        {isExpanded ? (
          <motion.div
            initial={{ y: -30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.1 }}
          >
            {renderExpandedContent()}
          </motion.div>
        ) : (
          <>
            {isCurrentlySignedUp ? (
              <CurrentlySignedUp signupItem={signupItem} signupType="items" />
            ) : null}
            <ProgressBarArea signupItem={signupItem} />
          </>
        )}
      </AnimatePresence>
    </SignupItemWrapper>
  );
}

const ExpandedContentMain = styled.div`
  margin-bottom: 10px;
  background: white;
  border-radius: 14px;
  margin: -2px -15px 15px -15px;
  padding: 3px;
  border: 1px solid #f0f0f0;
  box-shadow: 0 8px 24px 0 #eeeeeeff, 0 4px 8px 0 #42474c0f, 0 0 1px 0 #42474c52;
  //max-height: 400px;
  //overflow-y: auto;
`;

const SubmitWrapper = styled.div`
  display: flex;
  justify-content: space-between;

  @media (max-width: 590px) {
    margin-top: 10px;
  }
`;

const FieldsWrapper = styled.div`
  margin-right: 34px;
  margin-bottom: 34px;
`;